import AlertTriangle from "assets/img/svg/alertTriangle";
import NewForm from "components/NewForm";
import DeleteModal from "components/shared/DeleteModal";
import MessageBlock from "components/shared/MessageBlock";
import type { IFormStep } from "containers/TermFormContainer";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getUserListByCompany } from "store/adminPanelSlice";
import { fetchIntegrations } from "store/integrationsStore";
import type { IFetchSheetParams, IStatementTemplate } from "store/partnerStatementsStore";
import {
    deleteStatementTemplate,
    fetchGoogleSheetData,
    fetchGoogleSpreadSheets,
    fetchPartnerStatements,
    putStatementTemplate,
    setCollapsedSpreadsheet,
    setFetchGoogleSheetStatus,
    setGoogleSpreadsheets,
    setSelectedSheet,
} from "store/partnerStatementsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";

import variables from "../../../assets/styles/_variables.module.scss";
import { statementVariables, varNames } from "../statementVariables";
import type { INewStatementForm } from "../types";
import StatementConfig from "./StatementConfig";
import StatementIntegration from "./StatementIntegration/StatementIntegration";
import SubmitFormModal from "./SubmitFormModal/SubmitFormModal";

interface IStatementFormContainer {
    mode: "new" | "edit";
    /**
     * Data statement if mode = "edit"
     */
    type?: "statement";
    statementData?: IStatementTemplate | null;
    onCloseEditMode?: () => void;
    /**
     * Activate block modal when edit drawer
     */
    onDirtyDrawer?: (dirty: boolean) => void;
}

const StatementFormContainer = ({
    mode,
    statementData,
    onCloseEditMode,
    type,
    onDirtyDrawer,
}: IStatementFormContainer) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { getCompanySettings } = useCompanySettings();
    const usesMultipleCurrencies = getCompanySettings("uses_multiple_currencies");
    const alternativeCurrencies = JSON.parse(getCompanySettings("active_alternative_currencies"));
    const usesMetadata = getCompanySettings("uses_metadata");
    const { selectedSheet, googleSheetData, collapsedSpreadsheet } = useAppSelector((state) => state.partnerStatements);
    const integrations = useAppSelector((state) => state.integrations.integrations);
    const { customFields } = useAppSelector((state) => state.customFields);
    const hasSelectedSheet = selectedSheet.id != null;
    const users = useAppSelector((state) => state.adminPanel.users.company.data);
    const activeIntegrations = integrations.filter((integration) => integration.status == 2);
    const getIntegrationById = (integrationId: number) =>
        integrations.find((integration) => integration.id == integrationId);
    const iterativeLineWarningRef = useRef<HTMLDivElement>(null);
    const [hasValidIterationRange, setHasValidIterationRange] = useState(true); // check if iteration range is valid (columns template can change)

    const [submitFormModal, setSubmitFormModal] = useState<{ open: boolean; form: INewStatementForm | null }>({
        open: false,
        form: null,
    });
    const [isLoading, setIsLoading] = useState<TFetchStatus>("idle");
    const [openDeleteModal, setOpenDeleteModal] = useState(false);

    const formatVariables = useMemo(() => {
        const variables = [...statementVariables];
        if (usesMultipleCurrencies) {
            alternativeCurrencies.forEach((currency: string) => {
                variables.push(`exchangeRate:${currency}`);
            });
        }

        if (usesMetadata) {
            customFields.forEach((field) => {
                if (field.valueType !== "fileimport") {
                    variables.push(`meta:${field.uuid}`);
                }
            });
        }

        return variables;
    }, [alternativeCurrencies]);

    const formatVariablesNames = useMemo(() => {
        const variablesNames = { ...varNames };
        if (usesMultipleCurrencies) {
            alternativeCurrencies.forEach((currency: string) => {
                variablesNames[`exchangeRate:${currency}`] = t(`PartnerStatements.Variables.ExchangeRate`, {
                    currency,
                });
            });
        }

        if (usesMetadata) {
            customFields.forEach((field) => {
                if (field.valueType !== "fileimport") {
                    variablesNames[`meta:${field.uuid}`] = field.name;
                }
            });
        }

        return variablesNames;
    }, [alternativeCurrencies]);

    const defaultFormValues: INewStatementForm = useMemo(
        () => ({
            name: mode === "edit" ? statementData?.name : "",
            integration: mode === "edit" ? getIntegrationById(statementData?.integrationId as number) : null,
            type: "integration",
            searchSheet: "",
            searchSpreadsheet: "",
            spreadsheetId: mode === "edit" ? statementData?.data.spreadsheetId : "",
            sheetId: mode === "edit" ? statementData?.data.sheetId : null,
            iterationRange: mode === "edit" ? statementData?.data.iterationRange : "",
            variables: mode === "edit" ? (statementData?.data.variables as any) : null,
        }),
        [mode],
    );

    const methods = useForm({
        defaultValues: defaultFormValues,
        mode: "all",
    });

    const {
        handleSubmit,
        watch,
        trigger,
        setError,
        formState: { isDirty },
        setValue,
    } = methods;

    const integrationId = watch("integration")?.id;
    const spreadsheetId = watch("spreadsheetId");
    const sheetId = watch("sheetId");
    const iterationRange = watch("iterationRange");

    /**
     * If a a new column is added in the template, the iteration range can't have the same value
     * this detect changes and set isDirty to save the new iteration range
     */
    useEffect(() => {
        if (!googleSheetData.rowsData.length) return;
        const isValidIterationRange = googleSheetData.rowsData.some(
            (row: { line: number; range: string }) => row.range === iterationRange,
        );
        if (!isValidIterationRange) {
            setValue("iterationRange", undefined, { shouldDirty: true });
        }
        setHasValidIterationRange(isValidIterationRange);
    }, [googleSheetData, iterationRange]);

    useEffect(() => {
        if (mode === "edit") onDirtyDrawer && onDirtyDrawer(isDirty);
    }, [isDirty]);

    const fetchSheetParams: IFetchSheetParams = useMemo(() => {
        return {
            spreadsheetId: collapsedSpreadsheet?.id || (spreadsheetId as string),
            sheetId: (selectedSheet?.id as unknown as number) || (sheetId as number),
            integrationId: integrationId as number,
        };
    }, [collapsedSpreadsheet, selectedSheet, integrationId, mode]);

    useEffect(() => {
        if (mode === "new") {
            if (integrationId) {
                dispatch(fetchGoogleSpreadSheets(integrationId));
            } else {
                dispatch(setFetchGoogleSheetStatus("idle"));
            }
        } else if (mode === "edit") {
            dispatch(fetchGoogleSheetData(fetchSheetParams));
        }
        dispatch(setCollapsedSpreadsheet({ id: "", name: "" }));
        dispatch(setSelectedSheet({ id: null, name: "" }));
        dispatch(setGoogleSpreadsheets([]));
    }, [integrationId, mode]);

    useEffect(() => {
        if (mode === "new") {
            if (!users.length) dispatch(getUserListByCompany());
        }
        if (!integrations.length) dispatch(fetchIntegrations());
    }, [dispatch, mode]);

    // Action to search for another spreadsheet on edit mode
    const onValidateNewTemplate = () => {
        if (integrationId) dispatch(fetchGoogleSheetData(fetchSheetParams));
    };

    const steps: IFormStep[] = [
        {
            stepTitle: t("PartnerStatements.ConfigureStatement"),
            stepSubtitle: t("PartnerStatements.TemplateStatementChoice"),
            conditionToDisable: !hasSelectedSheet,
            format: (stepTitle: string, stepSubtitle: string) => (
                <StatementIntegration
                    activeIntegrations={activeIntegrations}
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    indexStep={0}
                    mode={mode}
                    onValidateNewTemplate={onValidateNewTemplate}
                />
            ),
        },
        {
            stepTitle: t("PartnerStatements.TemplateConfigure"),
            stepSubtitle: t("PartnerStatements.AssignValues"),
            conditionToDisable: googleSheetData.varCount == 0,
            format: (stepTitle: string, stepSubtitle: string) => (
                <StatementConfig
                    sheetFetchParams={fetchSheetParams}
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    variablesKeys={formatVariables}
                    variablesNames={formatVariablesNames}
                    indexStep={1}
                    mode={mode}
                    templateName={statementData?.name}
                    sheetName={selectedSheet.name}
                    spreadsheetName={collapsedSpreadsheet.name}
                    iterativeLineWarningRef={iterativeLineWarningRef}
                />
            ),
        },
    ];

    const onSubmit = async (form: INewStatementForm) => {
        if (mode === "new") onOpenSubmitFormModal(form);
        if (mode === "edit") {
            setIsLoading("pending");
            const reqData: IStatementTemplate = {
                name: form.name?.trim() as string,
                integrationId: form.integration?.id as number,
                type: form.type,
                data: {
                    spreadsheetId: form.spreadsheetId as string,
                    sheetId: form.sheetId as number,
                    iterationRange: form.iterationRange as string,
                    variables: form.variables as Record<string, string>,
                },
            };

            const response = await dispatch(
                putStatementTemplate({ id: statementData?.id as number, reqData: reqData }),
            );

            if (response.meta.requestStatus === "fulfilled") {
                await dispatch(fetchPartnerStatements());
                onCloseEditMode && onCloseEditMode();
                setIsLoading("succeeded");
            } else if (response.meta.requestStatus === "rejected") {
                setIsLoading("failed");
            }
        }
    };

    const onCloseSubmitFormModal = () => {
        setSubmitFormModal({ open: false, form: null });
    };

    const onOpenSubmitFormModal = (form: any) => {
        setSubmitFormModal({ open: true, form: form });
    };

    const dataForSteps = [`${collapsedSpreadsheet.name} <> ${selectedSheet.name}`, ""];

    const deleteStep = {
        title: t("PartnerStatements.DeleteStatement"),
        subtitle: t("PartnerStatements.DeleteStatementDesc"),
    };

    const onDeleteStatement = async () => {
        await dispatch(deleteStatementTemplate(statementData?.id as number));
        setOpenDeleteModal(false);
        onCloseEditMode && onCloseEditMode();
    };

    const handleScrollToErrors = () => {
        if (!iterationRange) {
            setError("iterationRange", { message: "no line selected", type: "required" });
            iterativeLineWarningRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    };

    const messageBlock = useMemo(() => {
        if (mode === "edit" && isDirty && !hasValidIterationRange) {
            return (
                <MessageBlock
                    text={t("PartnerStatements.InvalidIterationRange")}
                    type="error"
                    style={{ position: "sticky", zIndex: 5, bottom: "65px" }}
                />
            );
        }
        return null;
    }, [isDirty, hasValidIterationRange]);

    return (
        <FormProvider {...methods}>
            <div className="new-partner-statement">
                <DeleteModal
                    open={openDeleteModal}
                    onClose={() => setOpenDeleteModal(false)}
                    onDelete={onDeleteStatement}
                    description={t("PartnerStatements.DeleteStatementDesc")}
                    elementTitle={`"${statementData?.name as string}"`}
                />
                <NewForm
                    steps={steps}
                    useFormData={{ trigger, handleSubmit, isDirty }}
                    mode={mode}
                    onDelete={() => {
                        setOpenDeleteModal(true);
                    }}
                    closeEditMode={onCloseEditMode}
                    onSubmit={onSubmit}
                    deleteStep={deleteStep}
                    txtBtnSubmitNew={t("PartnerStatements.CreateStatement")}
                    dataForSteps={dataForSteps}
                    submitFormStatus={isLoading}
                    handleScrollToErrors={handleScrollToErrors}
                    type={type}
                    onCloseRoute={"/settings/partner-statements"}
                    messageBlock={messageBlock}
                />
                <SubmitFormModal
                    open={submitFormModal.open}
                    onClose={onCloseSubmitFormModal}
                    form={submitFormModal.form as INewStatementForm}
                />
            </div>
        </FormProvider>
    );
};

export default StatementFormContainer;
